import { FunctionComponent, useMemo, useState, useCallback, useEffect, useRef } from "react";

import { Box, Stack } from "@mui/material";
import {
  StripeRecurringInterval,
  getCurrencyFromCountry,
  convertTimestampToDateNumber,
  Undefinable,
  Utils,
  Emptyable,
  ProductDonationRecurrenceInterval,
  PaymentMethod,
  FormStatus,
  FormType,
  Locales,
  TicketingFormCategory,
  FeatureFlagName,
  CommandFormEmbedVersion,
  AmplitudeFlagKey,
  isQuebecOrganization,
  isBankDebitPaymentMethod,
  shouldPrioritizeBankPayment,
  CARD_MAXIMUM_AMOUNT_DEFAULT,
  Nullable,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { v4 } from "uuid";

import { IS_PREVIEW_QUERY_PARAM } from "../constants/url";
import { FrontendFormContext } from "../contexts/FrontendFormContext";
import { useLocaleContext } from "../contexts/LocaleContext";
import { GetCurrentUserFrontendObject } from "../contexts/types";
import { setUserProperty } from "../helpers/amplitude";
import { getDonationFormFeatureFlagTreatment } from "../helpers/feature-flag";
import { isDonationForm, isTicketing } from "../helpers/form";
import { getIsFreeTicketing, isPaymentMethodAlwaysComplete, listPaymentMethods } from "../helpers/payment-method";
import { trpc } from "../helpers/trpc";
import { useAmplitudeFeatureFlag } from "../hooks/amplitude/useAmplitudeFeatureFlag";
import { useTranslationField } from "../hooks/useDonationTranslation";
import { useFormAmplitudeContext, useLogFormAmplitude } from "../hooks/useFormAmplitudeContext";
import { useIsOnboarding } from "../hooks/useIsOnboarding";
import { useLoggedOrganization } from "../hooks/useLoggedOrganization";
import { useIsMobilePhone } from "../hooks/useIsMobilePhone";
import { useRepeatDonorRecognition } from "../hooks/useRepeatDonorRecognition";
import { useSupportsExpressCheckout } from "../hooks/useSupportsExpressCheckout";

import {
  DonationFormFrontendPageOutput,
  FormOrganizationOutput,
  TicketingFrontendPageOutput,
  TicketingOccurrenceOutput,
} from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { NotificationBanners } from "@/components/NotificationBanners/NotificationBanners";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { getStripeSubscriptionRecurrence } from "@/components/PaymentProcessor/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useComputeTip } from "@/components/PaymentProcessor/hooks/useComputeTip";

interface FrontendFormProvider {
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput;
  organization: FormOrganizationOutput;
  formType: FormType;
  isEmbed: boolean;
  themeColor?: Emptyable<string>;
  loggedOrganization: GetCurrentUserFrontendObject["currentOrganization"] | null | undefined;
  isDisplayOnly?: boolean;
}

export const FrontendFormProviderInner: FunctionComponent<React.PropsWithChildren<FrontendFormProvider>> = ({
  children,
  formData,
  organization,
  formType,
  isEmbed,
  themeColor,
  loggedOrganization,
  isDisplayOnly = false,
}) => {
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const embedId = searchParams.get("embedId");
  const isEmbedV2 = embedId !== null;
  const formEmbedVersion = isEmbedV2
    ? CommandFormEmbedVersion.EmbedV2
    : isEmbed
      ? CommandFormEmbedVersion.EmbedV1
      : null;
  const { mutate: updatePreviewFirstFormAt } = trpc.updatePreviewFirstFormAt.useMutation();

  const { data: shouldDisplayAddressQuestion } = trpc.form_shouldDisplayAddressQuestion.useQuery(
    {
      formId: formData.id,
      formType,
    },
    { enabled: !isDisplayOnly }
  );

  const { isOnboarding } = useIsOnboarding();
  const { locale } = useLocaleContext();
  const { amount: defaultAmount } = query as unknown as { amount: string };
  const search = useSearchParams();
  const isPreviewTemplateMode = search.get("previewTemplateMode") === "true" || false;
  const isPreview = search.get(IS_PREVIEW_QUERY_PARAM) === "true" || false;

  const organizationCreatedAtDate = organization.createdAtUtc
    ? convertTimestampToDateNumber(organization.createdAtUtc)
    : "undefined";

  const defaultDonationRecurrence = isDonationForm(formData)
    ? (formData.donationFormFields?.[0]?.donationFormAmounts?.[0]?.recurrenceInterval as
        | ProductDonationRecurrenceInterval
        | undefined) || ProductDonationRecurrenceInterval.OneTime
    : undefined;

  const defaultRecurrence = defaultDonationRecurrence
    ? getStripeSubscriptionRecurrence(defaultDonationRecurrence)
    : null;

  const [stripeRecurrenceInterval, setStripeRecurrenceInterval] = useState<StripeRecurringInterval | null>(
    defaultRecurrence
  );

  const isAuction = isTicketing(formData) && formData?.formCategory === TicketingFormCategory.Auction;
  const paymentMode = isAuction ? "setup" : stripeRecurrenceInterval ? "subscription" : "payment";

  const forceDisableExpressCheckout = searchParams.get("disableExpressCheckout") === "true";

  const { isActive: isAuctionExpressCheckoutEnabled } = useAmplitudeFeatureFlag({
    flagKey: AmplitudeFlagKey.AuctionExpressCheckout,
    skip: !isAuction,
  });

  const {
    expressCheckoutDisabled,
    isExpressCheckoutLoading,
    supportsExpressCheckout,
    handleSetSupportsExpressCheckout,
  } = useSupportsExpressCheckout(paymentMode, {
    forceDisabled: forceDisableExpressCheckout,
    allowSetupMode: isAuctionExpressCheckoutEnabled,
  });

  const defaultSelectedPaymentMethod =
    expressCheckoutDisabled || isPreviewTemplateMode ? PaymentMethod.Card : undefined;

  const commandId = useMemo(() => v4(), []);
  const dafExposureAmount = useRef<number | null>(null);
  const [dafFlow, setDafFlow] = useState<{ token: string; isResume: boolean; emailUpdateToken?: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPadLoading, setIsPadLoading] = useState(false);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<{
    paymentMethod: Undefinable<PaymentMethod>;
    complete: boolean;
  }>({
    paymentMethod: defaultSelectedPaymentMethod,
    complete: false,
  });

  useEffect(() => {
    if (loggedOrganization && !loggedOrganization.previewFirstFormAt && !isPreview && !isPreviewTemplateMode) {
      updatePreviewFirstFormAt();
    }
  }, [isPreview, isPreviewTemplateMode, loggedOrganization, updatePreviewFirstFormAt]);

  const [displayedFormAmount, setDisplayedFormAmount] = useState<number>(parseInt(defaultAmount) * 100 || 0);
  const [displayedFormTaxAmount, setDisplayedFormTaxAmount] = useState(0);
  const [shouldFetchTipSuggestions, setShouldFetchTipSuggestions] = useState(false);
  const [selectedOccurrence, setSelectedOccurrence] = useState<Nullable<TicketingOccurrenceOutput>>(null);

  const { repeatDonorToken, repeatDonorEmail, repeatDonorDeviceId } = useRepeatDonorRecognition();
  const isOnMobilePhone = useIsMobilePhone();
  const [hasDonorChangedAmount, setHasDonorChangedAmount] = useState(formType === FormType.Ticketing);
  const markAmountChangedByDonor = useCallback(() => setHasDonorChangedAmount(true), []);

  const translationLocales = getTranslationLocales(formData, formType, locale);
  const { field, postTransactionUrl, chequeDescription } = useTranslationField(formData);
  const { formAmplitudeContext } = useFormAmplitudeContext({
    formData,
    field,
    isEmbed,
    formEmbedVersion,
    organization,
    selectedPaymentMethod: selectedPaymentMethod.paymentMethod,
  });
  const currency = getCurrencyFromCountry(organization?.country);
  const isMembershipV2 = isTicketing(formData) && formData?.formCategory === TicketingFormCategory.MembershipV2;
  const isArchived = isDonationForm(formData) || isTicketing(formData) ? formData.isArchived : false;
  const isDraft = isDonationForm(formData) || isTicketing(formData) ? formData.status === FormStatus.Draft : false;
  const category = "formCategory" in formData ? formData.formCategory : formData.category;

  const isFreeTicketing = getIsFreeTicketing({ formData, displayedFormAmount });

  const isPaymentByPadAllowedByFeatureFlag = isDonationForm(formData)
    ? (getDonationFormFeatureFlagTreatment(formData.featureFlagTreatments, FeatureFlagName.IsPaymentByPadAllowed) ??
      false)
    : isTicketing(formData)
      ? true // IsPaymentByPadAllowed is always true for ticketing
      : false;

  const isBankPaymentAvailable = listPaymentMethods({
    formData,
    stripeRecurrenceInterval,
    selectedOccurrence,
    organizationCountry: organization.country,
    totalAmount: displayedFormAmount,
    formType,
    areBankPaymentMethodsAllowed: isPaymentByPadAllowedByFeatureFlag,
    orgCardMaximumAmount: organization.cardMaximumAmount ?? CARD_MAXIMUM_AMOUNT_DEFAULT,
    isAuction,
    isStripeCustomAccountActive: Boolean(organization.isStripeCustomAccountActive),
    isPreviewTemplateMode,
    isEmbed,
    isFreeTicketing,
    isApplePayGooglePayEnabledOnEmbed: false,
  }).some(isBankDebitPaymentMethod);

  const tipBaseAmount = isQuebecOrganization(organization?.country, organization?.region)
    ? displayedFormAmount - displayedFormTaxAmount
    : displayedFormAmount;

  const {
    tip,
    suggestedTips,
    defaultTipSuggestion,
    selectedTip,
    isSuggestedTipsAmount,
    handleChangeTips,
    handleChangeFreeTips,
    freeTips,
    updateFreeTipAmount,
    fees,
    formattedFees,
    tipExperiment,
    getTipChoice,
    logAmplitudeTipsChanged,
    displayTipSelection,
    prioritizeBankPayment: serverPrioritizeBankPayment,
    collapseRecommendedBankPayment: serverCollapseRecommendedBankPayment,
    paymentStepExperiment,
  } = useComputeTip({
    displayedFormAmount,
    tipBaseAmount,
    currency,
    organizationCountry: organization?.country,
    paymentMethod: selectedPaymentMethod.paymentMethod,
    isAuction,
    category,
    formData,
    shouldFetchTipSuggestions,
    hasDonorChangedAmount,
    repeatDonorToken,
    repeatDonorEmail,
    repeatDonorDeviceId,
    isRecurring: Boolean(stripeRecurrenceInterval),
    formType,
    stripeRecurrenceInterval,
    isMobile: isOnMobilePhone,
    isBankPaymentAvailable: shouldFetchTipSuggestions && isBankPaymentAvailable,
  });

  /**
   * Set the donor_organization_id and donor_organization_creation_date properties in amplitude.
   */
  useEffect(() => {
    if (organization.id) {
      setUserProperty("donor_organization_id", organization.id);
      setUserProperty("donor_organization_country", organization.country);
      setUserProperty("donor_organization_region", organization.region || "undefined");
      setUserProperty("donor_organization_creation_date", organizationCreatedAtDate);
      setUserProperty("donor_form_id", formData.id || "");
    }
  }, [organizationCreatedAtDate, organization.id, organization.country, organization.region, formData.id]);

  const isCashAppEnabled = false;

  useLogFormAmplitude({ formData, formAmplitudeContext });

  const { data: isApplePayGooglePayEnabledOnEmbed } = trpc.getIsApplePayGooglePayEnabledOnEmbed.useQuery(undefined, {
    enabled: isEmbed && !isDisplayOnly,
  });

  const handleSetSelectedPaymentMethod = useCallback(
    (paymentMethod: Undefinable<PaymentMethod>, complete?: boolean) => {
      setSelectedPaymentMethod({
        paymentMethod,
        complete: complete ?? isPaymentMethodAlwaysComplete(paymentMethod),
      });
    },
    []
  );

  const allowedPaymentMethods = useMemo(
    () =>
      listPaymentMethods({
        formData,
        stripeRecurrenceInterval,
        organizationCountry: organization.country,
        totalAmount: displayedFormAmount,
        totalAmountWithTip: displayedFormAmount + tip,
        formType,
        areBankPaymentMethodsAllowed: isPaymentByPadAllowedByFeatureFlag,
        orgCardMaximumAmount: organization.cardMaximumAmount ?? CARD_MAXIMUM_AMOUNT_DEFAULT,
        isAuction,
        isStripeCustomAccountActive: Boolean(organization.isStripeCustomAccountActive),
        isPreviewTemplateMode,
        isEmbed,
        isFreeTicketing,
        isApplePayGooglePayEnabledOnEmbed: Boolean(isApplePayGooglePayEnabledOnEmbed),
        isCashAppEnabled,
      }),
    [
      displayedFormAmount,
      tip,
      formData,
      formType,
      isAuction,
      isPaymentByPadAllowedByFeatureFlag,
      isPreviewTemplateMode,
      organization.cardMaximumAmount,
      organization.country,
      organization.isStripeCustomAccountActive,
      stripeRecurrenceInterval,
      isEmbed,
      isFreeTicketing,
      isApplePayGooglePayEnabledOnEmbed,
      isCashAppEnabled,
    ]
  );

  const applePayOrGooglePayAllowed = useMemo(
    () => allowedPaymentMethods.includes(PaymentMethod.ApplePayOrGooglePay),
    [allowedPaymentMethods]
  );

  const bankPaymentAllowed = allowedPaymentMethods.some(isBankDebitPaymentMethod);

  const prioritizeBankPayment =
    serverPrioritizeBankPayment ??
    shouldPrioritizeBankPayment({
      amount: displayedFormAmount,
      formType,
      organizationCountry: organization.country,
      stripeRecurrenceInterval,
    });
  const collapseRecommendedBankPayment =
    serverCollapseRecommendedBankPayment && allowedPaymentMethods.includes(PaymentMethod.Ach);

  const expressCheckoutIsSelected =
    supportsExpressCheckout && selectedPaymentMethod.paymentMethod === PaymentMethod.ApplePayOrGooglePay;

  const generateETicket =
    category &&
    [
      TicketingFormCategory.Event,
      TicketingFormCategory.Lottery,
      TicketingFormCategory.Sweepstakes,
      TicketingFormCategory.Custom,
      TicketingFormCategory.Shop,
    ].includes(category as TicketingFormCategory) &&
    isTicketing(formData) &&
    formData.generateQrCode;

  const displayExpressCheckout =
    applePayOrGooglePayAllowed && !isFreeTicketing && (supportsExpressCheckout || isExpressCheckoutLoading);

  const isPaymentDisabled = Boolean(organization.isPaymentDisabled);

  return (
    <FrontendFormContext.Provider
      value={{
        tipExperiment,
        isFreeTicketing,
        defaultSelectedPaymentMethod,
        formData,
        isArchived,
        isDraft,
        isOnboarding,
        organization,
        commandId,
        dafExposureAmount,
        dafFlow,
        setDafFlow,
        formType,
        category,
        themeColor,
        translationLocales,
        isEmbed,
        isEmbedV2,
        formEmbedVersion,
        embedId,
        formAmplitudeContext,
        selectedPaymentMethod: selectedPaymentMethod.paymentMethod,
        setSelectedPaymentMethod: handleSetSelectedPaymentMethod,
        currency,
        stripeRecurrenceInterval,
        setStripeRecurrenceInterval,
        tip,
        suggestedTips,
        defaultTipSuggestion,
        selectedTip,
        isSuggestedTipsAmount,
        handleChangeTips,
        handleChangeFreeTips,
        freeTips,
        updateFreeTipAmount,
        fees,
        formattedFees,
        displayTipSelection,
        displayedFormAmount,
        setDisplayedFormAmount,
        displayedFormTaxAmount,
        setDisplayedFormTaxAmount,
        tipBaseAmount,
        setShouldFetchTipSuggestions,
        setSelectedOccurrence,
        displayedFormAmountWithTip: displayedFormAmount + tip,
        isMembershipV2,
        areBankPaymentMethodsAllowed: bankPaymentAllowed,
        allowedPaymentMethods,
        postTransactionUrl,
        prioritizeBankPayment,
        collapseRecommendedBankPayment,
        paymentStepExperiment,
        markAmountChangedByDonor,
        selectedPaymentMethodIsBank: isBankDebitPaymentMethod(selectedPaymentMethod.paymentMethod),
        selectedPaymentMethodIsCard:
          selectedPaymentMethod.paymentMethod === PaymentMethod.Card ||
          selectedPaymentMethod.paymentMethod === PaymentMethod.ApplePayOrGooglePay,
        chequeDescription,
        showProceedButton:
          selectedPaymentMethod.paymentMethod === PaymentMethod.Pad ||
          (isBankDebitPaymentMethod(selectedPaymentMethod.paymentMethod) && selectedPaymentMethod.complete),
        isBankConnectionPending:
          selectedPaymentMethod.paymentMethod === PaymentMethod.Ach && !selectedPaymentMethod.complete,
        displayExpressCheckout,
        isCashAppEnabled,
        supportsExpressCheckout,
        handleSetSupportsExpressCheckout,
        isExpressCheckoutLoading,
        isSubmitting,
        setIsSubmitting,
        expressCheckoutIsSelected,
        expressCheckoutDisabled,
        paymentMode,
        isAuction,
        shouldSendTipPercentage: isAuction && !selectedTip.isFree && selectedTip.percentage !== 0,
        getTipChoice,
        logAmplitudeTipsChanged,
        displayAddressQuestion: shouldDisplayAddressQuestion ?? true,
        generateETicket,
        isPreviewTemplateMode,
        isPreview,
        isPaymentDisabled,
        isPadLoading,
        setIsPadLoading,
      }}
    >
      {children}
    </FrontendFormContext.Provider>
  );
};

const getTranslationLocales = (
  formData: Undefinable<DonationFormFrontendPageOutput | TicketingFrontendPageOutput>,
  formType: Undefinable<FormType>,
  locale: Locales
) => {
  return formType === FormType.DonationForm
    ? Utils.filterAndMap(
        (formData as DonationFormFrontendPageOutput).donationFormFields || [],
        (field) => field.locale !== locale,
        (field) => field.locale
      )
    : formType === FormType.Ticketing
      ? Utils.filterAndMap(
          (formData as TicketingFrontendPageOutput).ticketingFields || [],
          (field) => field.locale !== locale,
          (field) => field.locale
        )
      : [];
};

export const FrontendFormProvider = ({
  children,
  formType,
  isEmbed,
  themeColor,
  formData,
  organization,
  isDisplayOnly,
  hideNotificationBanners = false,
}: {
  children: React.ReactNode;
  formType?: FormType | null;
  isEmbed?: boolean;
  themeColor?: Emptyable<string>;
  formData?: DonationFormFrontendPageOutput | TicketingFrontendPageOutput | null;
  organization?: FormOrganizationOutput | null;
  isDisplayOnly?: boolean;
  hideNotificationBanners?: boolean;
}) => {
  const { loggedOrganization } = useLoggedOrganization();

  const content = (
    <Stack
      direction="column"
      sx={{
        height: "100vh",
        ["@supports (height: 100dvh)"]: {
          height: "100dvh",
        },
        position: "relative",
      }}
    >
      {!hideNotificationBanners && <NotificationBanners form={formData} loggedOrganization={loggedOrganization} />}

      <Box
        data-test="form-scrollable-area"
        sx={(theme) => ({
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          minHeight: 0,
          ...(isEmbed && {
            scrollbarWidth: "thin",
            scrollbarColor: `${theme.palette.text.neutral.supershy} transparent`,
            "&::-webkit-scrollbar": {
              width: theme.spacing(0.75),
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.text.neutral.supershy,
              borderRadius: theme.spacing(0.5),
            },
          }),
        })}
      >
        {children}
      </Box>
    </Stack>
  );

  return (
    <>
      {organization && formData && formType ? (
        <FrontendFormProviderInner
          formData={formData}
          organization={organization}
          formType={formType}
          isEmbed={isEmbed || false}
          themeColor={themeColor || ""}
          loggedOrganization={loggedOrganization}
          isDisplayOnly={isDisplayOnly}
        >
          {content}
        </FrontendFormProviderInner>
      ) : (
        <>{content}</>
      )}
    </>
  );
};
